import * as React from 'react';

import { PropertySource } from './property-source';

import { AlternateDatesModel, HotelViewModel } from 'models';

import { UitkCard, UitkCardContentSection } from 'uitk-react-cards';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkLayoutPosition } from 'uitk-react-layout-position';
import { UitkSpacing } from 'uitk-react-spacing';

import { isMemberOnlyDealListingFooter } from 'utils/listing-utils';

import { AdditionalInfo } from './additional-info';
import { AlternateDates } from './alternate-dates';
import { BadgeGenerator } from 'components/common/badge-generation';
import { CardLink } from './card-link';
import { HighightedPropertyDetails } from './highlighted-property-details';
import { ListingAmenities } from 'components/search/listing/internals/listing-amenities';
import { ListingCommonSection } from './listing-common-section';
import { ListingErrorBoundary } from './listing-error-boundary';
import { ListingFooter } from './footer';
import { ListingName } from './listing-name';
import { ListingPropertyBadges } from './listing-property-badges';
import { ListingTravelAd } from './listing-travel-ad';
import { Neighborhood } from './neighborhood';
import { PriceAndSupportingMessagesContainer } from './price-and-supporting-messages-container';
import { PrimaryBadge } from './primary-badge';
import { SearchListingPlaceholderLarge } from './placeholders';
import { Stars } from './star-rating';
import { TitleHeading } from './title-heading';

interface HorizontalListingProps {
  className: string;
  index?: number;
  loading?: boolean;
  model: HotelViewModel;
  defaultListingTopMarginClass?: string;
  stars?: number;
  alternateDatesProps?: {
    handleClick: () => void;
    model: AlternateDatesModel[];
  };
  // eslint-disable-next-line no-unused-vars
  getTitleComponent: (imageAspectRatio?: string, extraProps?: any) => any;
  imageAspectRatio?: string;
  rfrr?: string;
  alternateDatesInfo: {
    collapsedLinkName: string;
    collapsedMsgKey: string;
    collapsedRfrr: string;
    expandedLinkName: string;
    expandedMsgKey: string;
    expandedRfrr: string;
    headerTextKey: string;
  };
  dataStid?: string;
  cardLinkProps?: any;
  routeStoreGoBack?: () => void;
  listingClickHandler?: () => void;
  setItemRef?: () => void;
  mapListingView?: boolean;
}

export const HorizontalListing: React.FunctionComponent<HorizontalListingProps> = ({
  alternateDatesProps,
  index,
  loading,
  model,
  className,
  stars,
  getTitleComponent,
  imageAspectRatio,
  alternateDatesInfo,
  dataStid,
  cardLinkProps,
  routeStoreGoBack,
  listingClickHandler,
  setItemRef,
  mapListingView,
  rfrr,
}) => {
  const {
    amenities,
    alternateDates,
    listingFooter,
    name,
    neighborhood,
    pinnedDetails,
    price,
    propertyListingBadgeModel,
    propertySummary,
    propertySource,
    secondaryCardMessage,
    supportingMessages,
    thumbnail,
    travelAd,
    recentlyViewed,
    propertyDetailsLink,
  } = model;
  const listingWrapperProps = {
    className,
    'data-stid': dataStid || '',
    ref: setItemRef,
    tabIndex: -1,
  };
  listingWrapperProps.className += ' horizontal';

  const highlightedPropertyDetailsProps = {
    propertyDetails: propertySummary,
  };

  const propertySourceProps = propertySource;

  const secondary = propertyListingBadgeModel && propertyListingBadgeModel.secondary;
  const tertiary = propertyListingBadgeModel && propertyListingBadgeModel.tertiary;
  const pinned = Boolean(pinnedDetails?.heading);
  const displayListingFooter = isMemberOnlyDealListingFooter(listingFooter) || !mapListingView;

  const horizontalPriceAndSupportingMessagesContainerProps = {
    ...{ secondaryCardMessage, supportingMessages, price, secondary },
    infositeUrl: propertyDetailsLink?.uri?.relativePath,
    viewSmall: false,
    wrapPrice: false,
    priceFontWeight: '600',
    hotelId: model.hotelId,
    recentlyViewed,
    index,
  };

  if (loading) {
    return (
      <li {...listingWrapperProps}>
        <SearchListingPlaceholderLarge />
      </li>
    );
  }

  return (
    <li {...listingWrapperProps}>
      <ListingName visuallyHidden={true} />
      <UitkCard>
        {!mapListingView && pinned && <TitleHeading data-stid="title-heading" headingInfo={pinnedDetails} />}
        <UitkLayoutGrid columns={3}>
          <>
            <UitkLayoutGridItem colSpan={1}>
              <UitkLayoutPosition type="relative">
                <UitkLayoutFlex blockSize="full_size">
                  <ListingErrorBoundary thumbnail={thumbnail} name={name}>
                    {getTitleComponent(imageAspectRatio)}
                  </ListingErrorBoundary>
                </UitkLayoutFlex>
                <PrimaryBadge propertyListingBadgeModel={propertyListingBadgeModel} travelAd={travelAd} rfrr={rfrr} />
              </UitkLayoutPosition>
            </UitkLayoutGridItem>
            <UitkLayoutGridItem colSpan={2}>
              <UitkSpacing padding="three">
                <UitkCardContentSection className="listing-content">
                  <UitkLayoutFlex direction="column" blockSize="full_size">
                    <UitkLayoutFlexItem grow={1}>
                      <UitkLayoutGrid columns={1} justifyContent="start" alignContent="start">
                        <div>
                          <UitkLayoutGridItem colSpan={1}>
                            <ListingPropertyBadges />
                          </UitkLayoutGridItem>
                          <UitkLayoutGridItem colSpan={1}>
                            <ListingName lineCount={2} />
                          </UitkLayoutGridItem>
                          <UitkLayoutGridItem colSpan={1}>
                            <div>
                              <Stars stars={stars} />
                              <PropertySource {...propertySourceProps} />
                              <HighightedPropertyDetails {...highlightedPropertyDetailsProps} />
                              <Neighborhood neighborhood={neighborhood} />
                              <ListingAmenities amenities={amenities} />
                            </div>
                          </UitkLayoutGridItem>
                        </div>
                      </UitkLayoutGrid>
                    </UitkLayoutFlexItem>
                    <UitkLayoutFlexItem>
                      <UitkLayoutGrid columns={3} alignContent="end">
                        <>
                          <UitkLayoutGridItem colSpan={2} alignSelf="end">
                            <div>
                              <ListingTravelAd />
                              <ListingCommonSection routeStoreGoBack={routeStoreGoBack} />
                            </div>
                          </UitkLayoutGridItem>
                          <UitkLayoutGridItem colSpan={1} alignSelf="end" justifySelf="end">
                            <div>
                              <PriceAndSupportingMessagesContainer {...horizontalPriceAndSupportingMessagesContainerProps} />
                              {tertiary && tertiary.text && (
                                <div className="uitk-cell all-x-gutter-two uitk-type-right all-cell-shrink all-t-padding-half">
                                  <BadgeGenerator badge={tertiary} uniqueId={model.hotelId} />
                                </div>
                              )}
                            </div>
                          </UitkLayoutGridItem>
                        </>
                      </UitkLayoutGrid>
                    </UitkLayoutFlexItem>
                  </UitkLayoutFlex>
                </UitkCardContentSection>
              </UitkSpacing>
            </UitkLayoutGridItem>
          </>
        </UitkLayoutGrid>
        {displayListingFooter && (
          <ListingFooter
            {...cardLinkProps}
            data-stid="listing-footer"
            listingFooter={listingFooter}
            listingClickHandler={listingClickHandler}
          />
        )}
        {!mapListingView && alternateDates && (
          <AdditionalInfo {...alternateDatesInfo} displayHeaderText={true} keepOpen={true}>
            <AlternateDates data-stid="alternate-dates" {...alternateDatesProps} />
          </AdditionalInfo>
        )}
        <CardLink {...cardLinkProps} index={index} listingClickHandler={listingClickHandler} />
      </UitkCard>
    </li>
  );
};
